import React, { useState, useRef, useEffect } from 'react';

const AgeRangeSlider = ({ 
  minAge = 10, 
  maxAge = 65, 
  onChange, 
  disabled = false,
  className = '' 
}) => {
  // Define age range from 10 to 65+ (steps of 1)
  const minPossibleAge = 10;
  const maxPossibleAge = 65;
  
  const [minValue, setMinValue] = useState(Math.max(minPossibleAge, minAge));
  const [maxValue, setMaxValue] = useState(Math.min(maxPossibleAge, maxAge));
  const [anyAge, setAnyAge] = useState(false);
  const [isDragging, setIsDragging] = useState(null); // 'min', 'max', or null
  
  const sliderRef = useRef(null);
  const minThumbRef = useRef(null);
  const maxThumbRef = useRef(null);

  // Update values when props change
  useEffect(() => {
    setMinValue(Math.max(minPossibleAge, minAge));
    setMaxValue(Math.min(maxPossibleAge, maxAge));
  }, [minAge, maxAge]);

  // Notify parent of changes
  useEffect(() => {
    if (anyAge) {
      onChange?.(0, 65); // Any age means 0 to 65+
    } else {
      onChange?.(minValue, maxValue);
    }
  }, [minValue, maxValue, anyAge, onChange]);

  const getPercentage = (value) => {
    return ((value - minPossibleAge) / (maxPossibleAge - minPossibleAge)) * 100;
  };

  const getValueFromPercentage = (percentage) => {
    return Math.round(minPossibleAge + (percentage / 100) * (maxPossibleAge - minPossibleAge));
  };

  const handleMouseDown = (type) => (e) => {
    if (disabled || anyAge) return;
    e?.preventDefault();
    setIsDragging(type);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || disabled || anyAge || !sliderRef?.current) return;
    
    e?.preventDefault();
    const rect = sliderRef?.current?.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(100, ((e?.clientX - rect?.left) / rect?.width) * 100));
    const newValue = getValueFromPercentage(percentage);

    if (isDragging === 'min') {
      const constrainedValue = Math.min(newValue, maxValue);
      setMinValue(constrainedValue);
    } else if (isDragging === 'max') {
      const constrainedValue = Math.max(newValue, minValue);
      setMaxValue(constrainedValue);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(null);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, minValue, maxValue]);

  const handleTrackClick = (e) => {
    if (disabled || isDragging || anyAge) return;
    
    const rect = sliderRef?.current?.getBoundingClientRect();
    const percentage = ((e?.clientX - rect?.left) / rect?.width) * 100;
    const clickValue = getValueFromPercentage(percentage);
    
    // Determine which thumb is closer and move that one
    const minDistance = Math.abs(clickValue - minValue);
    const maxDistance = Math.abs(clickValue - maxValue);
    
    if (minDistance < maxDistance) {
      const constrainedValue = Math.min(clickValue, maxValue);
      setMinValue(constrainedValue);
    } else {
      const constrainedValue = Math.max(clickValue, minValue);
      setMaxValue(constrainedValue);
    }
  };

  const handleAnyAgeChange = (checked) => {
    setAnyAge(checked);
    if (checked) {
      // Set to 0-65+ when "Any Age" is checked
      setMinValue(minPossibleAge);
      setMaxValue(maxPossibleAge);
    }
  };

  const displayMinAge = anyAge ? 0 : minValue;
  const displayMaxAge = anyAge ? '65+' : (maxValue === maxPossibleAge ? '65+' : maxValue);

  return (
    <div className={`age-range-slider ${className}`}>
      {/* Range Display and Any Age Checkbox */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-blue-100">
            Age: {displayMinAge} - {displayMaxAge}
          </span>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="any_age"
              checked={anyAge}
              onChange={(e) => handleAnyAgeChange(e?.target?.checked)}
              disabled={disabled}
              className="w-4 h-4 text-indigo-600 bg-white bg-opacity-20 border-white border-opacity-30 rounded focus:ring-indigo-500 focus:ring-2"
            />
            <label htmlFor="any_age" className="text-sm font-medium text-blue-100">
              Any Age
            </label>
          </div>
        </div>
      </div>
      
      {/* Slider Track */}
      <div 
        ref={sliderRef}
        className={`relative h-6 bg-white bg-opacity-20 rounded-lg ${anyAge ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={handleTrackClick}
      >
        {/* Active Range */}
        <div
          className="absolute h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-lg"
          style={{
            left: `${getPercentage(minValue)}%`,
            width: `${getPercentage(maxValue) - getPercentage(minValue)}%`
          }}
        />
        
        {/* Min Thumb */}
        <div
          ref={minThumbRef}
          className={`absolute w-6 h-6 bg-white rounded-full shadow-lg cursor-grab transform -translate-y-0 -translate-x-1/2 border-2 border-orange-500 ${isDragging === 'min' ? 'cursor-grabbing scale-110' : ''} ${disabled || anyAge ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ left: `${getPercentage(minValue)}%` }}
          onMouseDown={handleMouseDown('min')}
        />
        
        {/* Max Thumb */}
        <div
          ref={maxThumbRef}
          className={`absolute w-6 h-6 bg-white rounded-full shadow-lg cursor-grab transform -translate-y-0 -translate-x-1/2 border-2 border-red-500 ${isDragging === 'max' ? 'cursor-grabbing scale-110' : ''} ${disabled || anyAge ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ left: `${getPercentage(maxValue)}%` }}
          onMouseDown={handleMouseDown('max')}
        />
      </div>
      
      {/* Age Labels */}
      <div className="flex justify-between mt-2 text-xs text-blue-200">
        <span>10</span>
        <span>20</span>
        <span>30</span>
        <span>40</span>
        <span>50</span>
        <span>60</span>
        <span>65+</span>
      </div>
    </div>
  );
};

export default AgeRangeSlider;